import { Bell, MessageCircle, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, useMatch } from 'react-router';

import { appNavStyles as styles } from './AppNav.styles';
import { type AppNavPresenterProps } from './AppNav.types';

import { BrandMark } from '@/components/atoms/BrandMark';
import { ROUTES } from '@/app/routes';
import { Avatar } from '@/components/atoms/Avatar';
import { cn } from '@/lib/cn';

/** Format large — rail latéral de 220 px (colonne de gauche des maquettes). */
export function AppNavRail({
  items,
  user,
  unread,
  onOpenNotifications,
  notificationsPanel,
  className,
}: Readonly<AppNavPresenterProps>) {
  const { t } = useTranslation('nav');
  // La carte entière s'allume quand on est sur le profil : c'est le conteneur
  // qui a besoin de l'information, pas le `NavLink` qui la porte d'habitude.
  const profilActif = useMatch(ROUTES.profile(user.username)) !== null;

  const secondary = [
    {
      to: ROUTES.notifications,
      labelKey: 'notifications',
      icon: Bell,
      count: unread.notifications,
    },
    {
      to: ROUTES.messages,
      labelKey: 'messages',
      icon: MessageCircle,
      count: unread.messages,
    },
  ] as const;

  return (
    <nav
      aria-label={t('aria.primary')}
      className={cn(styles.root({ layout: 'rail' }), className)}
    >
      <span className={styles.brand({ layout: 'rail' })}>
        <BrandMark className={styles.brandDot()} />
        Meeply
      </span>

      {items.map(({ to, labelKey, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          className={cn(styles.item({ layout: 'rail' }))}
        >
          <Icon aria-hidden className={styles.icon({ layout: 'rail' })} />
          {t(labelKey)}
        </NavLink>
      ))}

      <div className="mt-auto flex flex-col gap-1 border-t border-border pt-2.5">
        {secondary.map(({ to, labelKey, icon: Icon, count }) => {
          const label =
            count > 0
              ? `${t(labelKey)} — ${t('aria.unread', { count })}`
              : undefined;
          const inside = (
            <>
              <Icon aria-hidden className={styles.icon({ layout: 'rail' })} />
              {t(labelKey)}
              {count > 0 && (
                <span aria-hidden className={styles.badge({ layout: 'rail' })}>
                  {count}
                </span>
              )}
            </>
          );

          // The bell opens the drop-down here rather than navigating; the
          // page is one click further, behind "See all".
          if (labelKey === 'notifications' && onOpenNotifications) {
            return (
              <div key={to} className="relative">
                <button
                  type="button"
                  onClick={onOpenNotifications}
                  aria-label={label}
                  aria-haspopup="dialog"
                  className={cn(styles.item({ layout: 'rail' }), 'w-full')}
                >
                  {inside}
                </button>
                {notificationsPanel}
              </div>
            );
          }

          return (
            <NavLink
              key={to}
              to={to}
              className={cn(styles.item({ layout: 'rail' }))}
              aria-label={label}
            >
              {inside}
            </NavLink>
          );
        })}

        {/* Deux cibles distinctes, donc deux liens FRÈRES : la carte mène au
            profil, l'engrenage aux réglages. Un lien dans un lien serait du
            HTML invalide et rendrait le clavier incohérent.

            L'état actif du profil est porté par le CONTENEUR, pas par le lien :
            c'est toute la carte qui doit devenir bleue, l'engrenage compris. */}
        <div
          className={cn(
            'mt-2 flex items-center gap-1 rounded-xl p-1 transition-colors',
            profilActif ? 'bg-accent-soft' : 'bg-surface',
          )}
        >
          <NavLink
            to={ROUTES.profile(user.username)}
            className={cn(
              styles.item({ layout: 'rail' }),
              'min-w-0 flex-1 px-1.5 py-1.5',
              // Le fond actif est déjà peint par la carte : le lien n'en pose
              // pas un second, plus foncé, par-dessus.
              'aria-[current=page]:bg-transparent',
            )}
          >
            <Avatar src={user.avatarUrl} name={user.displayName} size="sm" />
            <span className="min-w-0 flex-1 text-left">
              <span className="block truncate font-semibold">
                {user.displayName}
              </span>
              <span className="block text-2xs text-fg-subtle">
                {t('profile')}
              </span>
            </span>
          </NavLink>

          <NavLink
            // Pas de `end` : l'engrenage s'allume sur toute la sous-arborescence
            // des réglages. `/settings` redirige vers une section, il ne serait
            // donc jamais l'URL courante — avec `end`, l'icône resterait
            // éteinte en permanence.
            to={ROUTES.settings}
            aria-label={t('settings')}
            className={cn(
              styles.item({ layout: 'rail' }),
              'shrink-0 p-2',
              // Rotation au survol : l'engrenage est la seule icône de l'app
              // qui suggère un mécanisme, autant s'en servir. Coupée par
              // `[data-reduce-motion]`, comme toute transition.
              '[&_svg]:transition-transform [&_svg]:duration-200',
              'hover:[&_svg]:rotate-90 focus-visible:[&_svg]:rotate-90',
            )}
          >
            <Settings aria-hidden className="size-4 shrink-0" />
          </NavLink>
        </div>
      </div>
    </nav>
  );
}
